import { cva, type VariantProps } from 'class-variance-authority'
import { Calendar, GripVertical, Hash, Link2, type LucideIcon, Sigma, Type, X } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { Icon } from './Icon'

/**
 * ColumnChip — a draggable pill representing one column from an MV in the
 * Phase-2 Report Builder.
 *
 * The chip is intentionally HEADLESS w.r.t. drag-and-drop: it accepts visual
 * state (isDragging, isOver) as props and the consuming page wires
 * `useDraggable`/`useSortable` around it. This keeps `@intelliauth/ui` free
 * of `@dnd-kit/*` runtime dependency and lets Storybook stories render
 * without a DndContext wrapper.
 *
 * Roles map to glyphs (so the user can tell measures from dims at a glance):
 *   dimension → ◇  (Hash icon)
 *   measure   → Σ  (Sigma icon)
 *   time      → ⌚  (Calendar icon)
 *   fk        → 🔗 (Link icon)
 *
 * When `agg` is set (e.g., "sum"), the chip prepends "Σ" and renders as a
 * "Σ success_count" label. Used inside PivotDropZone "Values" slot.
 */

export type ColumnRole = 'dimension' | 'measure' | 'time' | 'fk'

const chipVariants = cva(
  'inline-flex items-center gap-1.5 rounded-md border bg-card px-2 py-1 text-caption text-ia-text shadow-sm transition-colors',
  {
    variants: {
      tone: {
        neutral: 'border-ia-border',
        dimension: 'border-ia-border',
        measure: 'border-ia-info/40',
        time: 'border-ia-warning/40',
        fk: 'border-ia-success/40',
      },
      interactive: {
        true: 'cursor-grab hover:bg-ia-elevated active:cursor-grabbing',
        false: 'cursor-default',
      },
      isDragging: {
        true: 'opacity-40 ring-2 ring-primary/40',
        false: '',
      },
    },
    defaultVariants: { tone: 'neutral', interactive: true, isDragging: false },
  },
)

const roleIconMap: Record<ColumnRole, LucideIcon> = {
  dimension: Hash,
  measure: Sigma,
  time: Calendar,
  fk: Link2,
}

const roleToTone: Record<ColumnRole, NonNullable<VariantProps<typeof chipVariants>['tone']>> = {
  dimension: 'dimension',
  measure: 'measure',
  time: 'time',
  fk: 'fk',
}

export interface ColumnChipProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'role'> {
  /** The display label (column name or alias). */
  label: string
  /** Schema role — drives the icon + accent color. */
  columnRole: ColumnRole
  /** Optional aggregator label rendered before the column ("sum", "count"…). */
  agg?: string
  /** Source MV name — shown small + muted under the label when provided. */
  source?: string
  /** Visual-only "I'm being dragged" state — caller controls. */
  isDragging?: boolean
  /** When true, renders a left-side grip handle to signal draggability. */
  draggable?: boolean
  /** Click handler for the trailing X. Omit to hide the X. */
  onRemove?: () => void
  /** Disabled chips appear muted and don't show the grip cursor. */
  disabled?: boolean
  /** Replace the default role glyph with a custom icon. */
  icon?: LucideIcon
  /** Optional aria label override (defaults to label). */
  'aria-label'?: string
}

export const ColumnChip = React.forwardRef<HTMLDivElement, ColumnChipProps>(
  (
    {
      className,
      label,
      columnRole,
      agg,
      source,
      isDragging,
      draggable = true,
      onRemove,
      disabled,
      icon,
      ...props
    },
    ref,
  ) => {
    const RoleIcon: LucideIcon = icon ?? roleIconMap[columnRole] ?? Type
    const tone = roleToTone[columnRole] ?? 'neutral'
    return (
      <div
        ref={ref}
        className={cn(
          chipVariants({ tone, interactive: !disabled && draggable, isDragging }),
          disabled && 'opacity-50 cursor-not-allowed',
          className,
        )}
        {...props}
      >
        {draggable && !disabled && (
          <GripVertical aria-hidden className="h-3 w-3 text-ia-text-tertiary shrink-0" />
        )}
        <Icon icon={RoleIcon} size="xs" className="text-ia-text-secondary shrink-0" aria-hidden />
        {agg && (
          <span className="text-caption font-medium text-ia-info uppercase tracking-wide">
            {agg}
          </span>
        )}
        <span className="font-medium truncate">{label}</span>
        {source && (
          <span className="text-ia-text-tertiary text-[11px] truncate hidden sm:inline">
            {source}
          </span>
        )}
        {onRemove && !disabled && (
          <button
            type="button"
            aria-label={`Remove ${label}`}
            onClick={(e) => {
              e.stopPropagation()
              onRemove()
            }}
            className="ml-1 rounded p-0.5 text-ia-text-tertiary hover:bg-ia-elevated hover:text-ia-text"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    )
  },
)
ColumnChip.displayName = 'ColumnChip'
